import {createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import {NotificationState} from './notification.reducer';
import {INotification, INotificationPerStore, INotificationTotals} from '../models';
import {dateToFirebaseTimestamp} from '../../../shared/shared-utils/firestore/firestore.utils';
import {IStockUpdate} from '../models';

/**
 * Selector to retrieve the notification state slice.
 *
 * @returns {NotificationState} The notification feature state.
 */
export const selectNotificationState = createFeatureSelector<NotificationState>('notification');

//#region SELECT LOADING

/**
 * Selector to determine if user notifications are currently loading.
 *
 * @param state - Notification state.
 * @returns {boolean} True if loading user notifications.
 */
export const selectIsUserNotificationsLoading = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.isUserNotificationsLoading,
);

/**
 * Selector to check if notification update process is loading.
 *
 * @returns {boolean} True if notification update is in progress.
 */
export const selectIsUpdateNotificationsLoading = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.isUpdateNotificationLoading,
);

/**
 * Selector for loading state of auto-order notifications.
 *
 * @returns {boolean} True if auto-order notification fetching is in progress.
 *
 * @example
 * ```ts
 * store.select(selectIsGetNotificationAutoOrderLoading).subscribe(isLoading => console.info(isLoading));
 * ```
 */
export const selectIsGetNotificationAutoOrderLoading = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.isGetNotificationAutoOrderLoading,
);

/**
 * Selector for loading state of stock update notifications.
 *
 * @returns {boolean} True if stock update notifications are being fetched.
 *
 * @example
 * ```ts
 * store.select(selectIsGetNotificationStockUpdateLoading).subscribe(isLoading => console.info(isLoading));
 * ```
 */
export const selectIsGetNotificationStockUpdateLoading = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.isGetNotificationStockUpdateLoading,
);

/**
 * Selector for loading state of user scroll notifications.
 *
 * @returns {boolean} True if user scroll notifications are loading.
 */
export const selectIsUserScrollGetLoading = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.isUserScrollLoading,
);

//#endregion

//#region SELECT COUNTERS

/**
 * Selector to get all notification totals.
 *
 * @returns {object} Notification totals containing total, read, and unread counts.
 */
export const selectNotificationPaginatedTotals = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.notificationPaginatedTotals,
);

export const selectNotificationUnreadTotals = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.notificationUnreadTotals,
);

export const selectNotificationUnreadFailedTotals = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.notificationUnreadFailedTotals,
);

export const selectNotificationTotalsAggregated = createSelector(
  selectNotificationPaginatedTotals,
  selectNotificationUnreadTotals,
  selectNotificationUnreadFailedTotals,
  (paginated: INotificationTotals, unread: INotificationTotals, failed: INotificationTotals) => {
    return {
      failed: paginated.failed,
      total: paginated.total,
      read: paginated.read,
      unread: paginated.unread,
      unreadFailed: paginated.unreadFailed,
    } as INotificationTotals;
  },
);

//#endregion

//#region SELECT LAST DOCUMENT ID

/**
 * Selector to get the last document ID used for pagination.
 *
 * @returns {string | undefined} Last document ID or undefined if none.
 */
export const selectNotificationLastDocumentId = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.lastDocumentId,
);

//#endregion

//#region SELECT FILTER CHIPS

/**
 * Selector to get the current filter chips applied to notifications.
 *
 * @returns {INotificationChips} Current notification filter chips.
 */
export const selectFilterChips = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.filterChips,
);

//#endregion

//#region SELECT NOTIFICATIONS

/**
 * Selector to retrieve all notifications from state.
 *
 * @returns {INotification[]} Array of all notifications.
 */
export const selectAllNotifications = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.notificationsPaginatedGrouped,
);

/**
 * Selector to retrieve notifications per store by store ID.
 *
 * @param storeId - Store ID to select notifications for.
 * @returns {MemoizedSelector<object, INotificationPerStore>} Notifications per store.
 */
export const selectNotificationsPerStoreByStoreId =
  (storeId: string): MemoizedSelector<object, INotificationPerStore> => createSelector(
    selectNotificationState,
    (state: NotificationState) => state.notificationsPaginatedGrouped[storeId] || {} as INotificationPerStore,
  );

/**
 * Selector to retrieve notifications list by store ID.
 *
 * @param storeId - Store ID to select notifications for.
 * @returns {MemoizedSelector<object, INotification[]>} Notifications array for the store.
 */
export const selectNotificationsByStoreId =
  (storeId: string): MemoizedSelector<object, INotification[]> => createSelector(
    selectNotificationState,
    (state: NotificationState) => state.notificationsPaginatedGrouped[storeId]?.notifications || [],
  );

//#endregion

//#region SELECT AUTO ORDERS

/**
 * Selector for all auto-order notifications.
 *
 * @returns {IAutoOrderOrder[]} Array of auto-order notifications.
 *
 * @example
 * ```ts
 * store.select(selectAllNotificationAutoOrders).subscribe(autoOrders => console.info(autoOrders));
 * ```
 */
export const selectAllNotificationAutoOrders = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.notificationAutoOrders,
);

//#endregion

//#region SELECT STOCK UPDATES

/**
 * Selector for a temporary stock update notification object.
 *
 * This returns a temporary object with current timestamps and placeholder order ID.
 *
 * @returns {IStockUpdate} Temporary stock update.
 *
 * @example
 * ```ts
 * store.select(selectAllNotificationStockUpdates).subscribe(stockUpdate => console.info(stockUpdate));
 * ```
 */
export const selectAllNotificationStockUpdates = createSelector(
  selectNotificationState,
  (): IStockUpdate => {
    const temp: IStockUpdate = {
      executed: dateToFirebaseTimestamp(new Date()),
      generated: dateToFirebaseTimestamp(new Date()),
      orderId: 'Np order ID',
    };
    return temp;
  },
);

//#endregion

//#region SELECT SELECTED

/**
 * Selector for currently selected notification.
 *
 * @returns {INotification | undefined} The selected notification object.
 */
export const selectSelectedNotification = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.selected.notification,
);

/**
 * Selector for changes in the selected stock update view.
 *
 * @returns {INotification | undefined} Changes associated with selected stock update.
 */
export const selectSelectedStockViewChanges = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.stockViewChanges,
);

/**
 * Selector to determine if stock update changes are loading.
 *
 * @returns {boolean} True if loading stock update changes.
 */
export const selectIsGetStockUpdateChangesLoading = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.isGetStockUpdateChangesLoading,
);

export const selectRetryStockUpdateValue = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.retryStockUpdate,
);

export const selectRetryExcludeFailedBarcodes = createSelector(
  selectNotificationState,
  (state: NotificationState) => state.retryExcludeFailedBarcodes,
);

export const selectViewChangesModalTitle = createSelector(
  selectRetryStockUpdateValue,
  selectRetryExcludeFailedBarcodes,
  (retryStockUpdate: boolean, excludeFailedBarcodes: boolean): string => {
    if (!retryStockUpdate) {
      return 'View Changes';
    }
    if (excludeFailedBarcodes) {
      return 'Retry Without Barcodes';
    }
    return 'Retry Stock Update';
  },
);

//#endregion
